import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Image as ImageIcon, Mail, Map as MapIcon, MapPin, Phone, Store } from "lucide-react";
import client, { apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { SHOWROOM_DIALOG_IMAGE } from "@/lib/stockImages";
import LocationPicker from "./LocationPicker";
import ShowroomImages from "./ShowroomImages";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEXT_FIELDS = ["name", "phone", "email", "city", "address"];
const REQUIRED = ["name", "phone", "city", "address"];

const HIGHLIGHTS = [
  [Store, "Manage multiple locations", "Add and update your showroom details effortlessly."],
  [MapPin, "Help customers find you", "Add the address and a map pin so your team can share directions."],
  [ImageIcon, "Show what's inside", "Upload photos to give a glimpse of your space."],
];

const formFor = (showroom) => ({
  ...Object.fromEntries(TEXT_FIELDS.map((k) => [k, showroom?.[k] || ""])),
  location: showroom?.latitude != null && showroom?.longitude != null ? { latitude: showroom.latitude, longitude: showroom.longitude } : null,
  images: (showroom?.images || []).map((img) => ({ key: img.fileId, fileId: img.fileId, url: img.url, uploading: false })),
});

function Field({ label, required, htmlFor, children, error }) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="block text-sm text-foreground mb-1.5">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
}

const inputClass =
  "w-full rounded-lg border border-border bg-card pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20";

export default function ShowroomDialog({ open, onOpenChange, showroom }) {
  const queryClient = useQueryClient();
  const editing = Boolean(showroom);
  const [form, setForm] = useState(formFor(null));
  const [seededFor, setSeededFor] = useState(null);

  // Reset the form each time the dialog opens for a different showroom (or a new one).
  const seedKey = open ? showroom?._id || "new" : null;
  if (seedKey !== seededFor) {
    setSeededFor(seedKey);
    if (seedKey) setForm(formFor(showroom));
  }

  const save = useMutation({
    mutationFn: (payload) => (editing ? client.patch(`/showrooms/${showroom._id}`, payload) : client.post("/showrooms", payload)),
    onSuccess: () => {
      toast.success(editing ? "Showroom updated" : "Showroom created");
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["showrooms"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the showroom")),
  });

  const emailInvalid = form.email.trim() && !EMAIL_RE.test(form.email.trim());
  const uploading = form.images.some((img) => img.uploading);
  const ready = REQUIRED.every((k) => form[k].trim()) && !emailInvalid && !uploading;

  const submit = () => {
    const payload = Object.fromEntries(TEXT_FIELDS.map((k) => [k, form[k].trim()]));
    // A blank string fails the backend's email check; null clears it on edit, and a new showroom just omits it.
    if (!payload.email) {
      if (editing) payload.email = null;
      else delete payload.email;
    }
    if (form.location) Object.assign(payload, form.location);
    else if (editing) Object.assign(payload, { latitude: null, longitude: null });
    payload.imageFileIds = form.images.map((img) => img.fileId).filter(Boolean);
    save.mutate(payload);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const textInput = (name, Icon, props) => (
    <div className="relative">
      <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
      <input id={`showroom-${name}`} data-testid={`showroom-${name}-input`} value={form[name]} onChange={set(name)} className={cn(inputClass, "h-11")} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="add-showroom-dialog"
        className="max-w-5xl p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl bg-card md:grid-cols-[2fr_3fr] [&>button:last-child]:right-5 [&>button:last-child]:top-5"
      >
        <aside className="hidden md:flex flex-col bg-sidebar">
          <img src={SHOWROOM_DIALOG_IMAGE} alt="" className="h-64 w-full object-cover rounded-br-[3rem]" />
          <div className="px-8 pt-8 pb-10">
            <p className="font-heading text-[32px] leading-tight text-foreground">{editing ? "Your Showroom" : "Add a Showroom"}</p>
            <p className="mt-2 text-foreground/70 leading-relaxed">
              Showcase your physical location and make it easy for customers to visit, explore and connect.
            </p>
            <ul className="mt-7 space-y-5">
              {HIGHLIGHTS.map(([Icon, title, text]) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-full bg-card flex items-center justify-center text-accent shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="flex flex-col max-h-[92vh] min-w-0">
          <div className="px-6 sm:px-9 pt-7 pb-4 pr-14">
            <DialogTitle className="font-heading text-2xl sm:text-[28px] font-normal leading-tight text-foreground">
              {editing ? "Edit Showroom" : "Add Showroom"}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              {editing ? "Update the details of this showroom." : "Add the details of your physical showroom."}
            </DialogDescription>
          </div>

          <div className="flex-1 overflow-y-auto px-6 sm:px-9 pb-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <Field label="Showroom Name" required htmlFor="showroom-name">
                {textInput("name", Store, { placeholder: "e.g. Mumbai Showroom" })}
              </Field>
              <Field label="Phone Number" required htmlFor="showroom-phone">
                {textInput("phone", Phone, { placeholder: "e.g. +91 98765 43210", type: "tel" })}
              </Field>
              <Field label="Email Address" htmlFor="showroom-email" error={emailInvalid && "Enter a valid email address."}>
                {textInput("email", Mail, { placeholder: "e.g. mumbai@yourshop.com", type: "email" })}
              </Field>
              <Field label="City" required htmlFor="showroom-city">
                {textInput("city", MapPin, { placeholder: "e.g. Mumbai" })}
              </Field>
            </div>
            <Field label="Full Address" required htmlFor="showroom-address">
              <div className="relative">
                <MapIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
                <textarea
                  id="showroom-address"
                  data-testid="showroom-address-input"
                  rows={2}
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Enter the complete address, with area and PIN code"
                  className={cn(inputClass, "py-3 resize-y")}
                />
              </div>
            </Field>
            <Field label="Map Location">
              <LocationPicker
                value={form.location}
                onChange={(location) => setForm((f) => ({ ...f, location }))}
                address={[form.address, form.city].filter((v) => v.trim()).join(", ")}
              />
            </Field>
            <Field label="Showroom Images">
              <ShowroomImages images={form.images} setImages={(fn) => setForm((f) => ({ ...f, images: fn(f.images) }))} />
            </Field>
          </div>

          <div className="px-6 sm:px-9 py-5 border-t border-border flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button variant="outline" className="h-11 rounded-xl px-10" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="showroom-submit-button"
              disabled={!ready || save.isPending}
              className="h-11 rounded-xl px-8 bg-accent hover:bg-accent/90 text-accent-foreground"
              onClick={submit}
            >
              {save.isPending ? "Saving…" : uploading ? "Uploading photos…" : editing ? "Save Changes" : "Create Showroom"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
